<template>
  <div ref="messagesContainer" class="flex-1 overflow-y-auto p-4 space-y-4 chat-scrollbar">
    <div v-for="message in messages" :key="message.id" class="flex gap-3" :class="message.role === 'user' ? 'justify-end' : ''">
      <div v-if="message.role === 'assistant'" class="flex-shrink-0">
        <div class="size-8 rounded-full bg-yellow-500 flex items-center justify-center">
          <UIcon name="i-lucide-bot" class="size-4 text-white" />
        </div>
      </div>
      
      <div class="flex-1 max-w-[80%]" :class="message.role === 'user' ? 'order-first' : ''">
        <div 
          class="rounded-lg px-3 py-2"
          :class="getMessageClasses(message)"
        >
          <p class="text-sm whitespace-pre-wrap">{{ message.content }}</p>
        </div>
        <p class="text-xs text-gray-500 mt-1" :class="message.role === 'user' ? 'text-right' : ''">
          {{ formatTime(message.timestamp) }}
        </p>
      </div>

      <div v-if="message.role === 'user'" class="flex-shrink-0">
        <div class="size-8 rounded-full bg-gray-300 flex items-center justify-center">
          <UIcon name="i-lucide-user" class="size-4 text-gray-700" />
        </div>
      </div>
    </div>

    <!-- Typing Indicator -->
    <div v-if="isTyping" class="flex gap-3">
      <div class="flex-shrink-0">
        <div class="size-8 rounded-full bg-yellow-500 flex items-center justify-center">
          <UIcon name="i-lucide-bot" class="size-4 text-white" />
        </div>
      </div>
      <div class="bg-gray-100 rounded-lg px-3 py-2">
        <div class="flex gap-1">
          <span class="size-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0ms"></span>
          <span class="size-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 150ms"></span>
          <span class="size-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 300ms"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, watch } from 'vue'

const props = defineProps({
  messages: {
    type: Array,
    required: true
  },
  isTyping: {
    type: Boolean,
    default: false
  }
})

const messagesContainer = ref(null)

const formatTime = (date) => {
  return new Intl.DateTimeFormat('de-DE', {
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

const getMessageClasses = (message) => {
  if (message.role === 'user') {
    return 'bg-yellow-500 text-white'
  }
  
  // Assistant messages with different types
  switch (message.type) {
    case 'welcome':
      return 'bg-blue-50 text-blue-900 border border-blue-200'
    case 'angebot':
      return 'bg-green-50 text-green-900 border border-green-200'
    case 'aufmass':
      return 'bg-orange-50 text-orange-900 border border-orange-200'
    case 'rechnung':
      return 'bg-purple-50 text-purple-900 border border-purple-200'
    default:
      return 'bg-gray-100 text-gray-900'
  }
}

const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}

watch(() => props.messages, () => {
  scrollToBottom()
}, { deep: true })

watch(() => props.isTyping, () => {
  if (props.isTyping) {
    scrollToBottom()
  }
})

defineExpose({
  scrollToBottom
})
</script>